import { Injectable } from '@angular/core'
import { map, Observable, take } from 'rxjs'
import { Statistic, StatisticResponse, StatisticState, StatisticWithDisplayData, StatisticWithForm } from '@app/models/statistic.model'
import { TranslateService } from '@ngx-translate/core'
import { omit } from 'lodash-es'
import { GET_KPIS } from '@app/query/statistic.query'
import { ModulesType } from '@app/models/client.model'
import { ApiService } from '@app/core/graphql/services/api.service'
import { UsersService } from '@app/shared/pages/settings/services/users.service'
import { DateService } from '@app/shared/services/date.service'

@Injectable()
export class StatisticService {
  constructor(private API: ApiService, private translate: TranslateService, private userService: UsersService, private dateService: DateService) {}
  getEventSizeList(statistic: Statistic): Array<any> {
    return statistic.event_size
      .filter((el: string) => el != 'custom')
      .map((el) => {
        return {
          value: el,
          name: this.translate.instant('DASHBOARD.TOTAL_MAX_LIST.UP_TO') + ' ' + el.split('-').pop(),
        }
      })
  }
  getPeriodList(statistic: Statistic): Array<any> {
    return statistic.time_frame.map((el) => {
      return {
        value: el,
        name: this.translate.instant('DASHBOARD.PERIOD_LIST.' + el.toUpperCase()),
      }
    })
  }

  /**
   * Returns the saved ID for the user
   * @param activeModuleName The current module
   * @returns The saved for for the user
   */
  getUserStatistic(activeModuleName: ModulesType): Promise<Statistic[]> {
    return new Promise((done) => {
      this.getStatistics(activeModuleName).then((stats) => {
        this.userService
          .getCurrentUserPrefs()
          .pipe(take(1))
          .subscribe((prefs: any) => {
            if (prefs.KPIs?.length) {
              /* let slugs = prefs.KPIs.map((el: string) => el.trim());
              let statistis = stats.filter((el: Statistic) =>
                slugs.includes(el.id)
              ); */
              const statistis = stats.filter((v) => prefs.KPIs.includes(v.name))
              if (statistis.length) {
                done(statistis)
              } else {
                done([])
              }
            } else {
              done([])
            }
          })
      })
    })
  }

  /**
   * Must save the state in form group
   * @param statistics Statistics
   * @returns
   */
  savePreferredLocalStatistics(statistics: Array<StatisticWithForm>): Promise<undefined> {
    return new Promise((done: any) => {
      this.userService
        .getCurrentUser()
        .pipe(take(1))
        .subscribe((user) => {
          window.localStorage.setItem(
            'preferredStatistics',
            JSON.stringify({
              user_id: user.id,
              statistics: statistics.map((el) => {
                return {
                  id: el.name,
                  value: el.form.value,
                }
              }) as Array<StatisticState>,
            })
          )
          done()
        })
    })
  }

  getPreferredLocalStatistics(): Promise<Array<StatisticState>> {
    return new Promise((done) => {
      this.userService
        .getCurrentUser()
        .pipe(take(1))
        .subscribe((data) => {
          const user_id = parseInt(data.id as string)
          const state: any = window.localStorage.getItem('preferredStatistics')
          let response: Array<StatisticState> = []
          if (state) {
            try {
              const res = JSON.parse(state)
              if ('object' == typeof res && parseInt(res.user_id) == user_id) {
                response = res.statistics
              }
            } catch (_e) {
              // Malformed stored preferences — ignore and fall back to empty list
            }
          }
          done(response)
        })
    })
  }

  savePreferredStatistics(statistics: Array<StatisticWithForm>): Observable<any> {
    return new Observable((observer) => {
      this.userService
        .saveCurrentUserPrefs({
          KPIs: statistics.map((el) => el.name).join(','),
          //  category: statistics.map((el) => el.form.get('category')!.value)
        })
        .pipe(take(1))
        .subscribe(() => {
          this.savePreferredLocalStatistics(statistics).then(() => {
            observer.next(true)
          })
        })
    })
  }

  getStatisticData(statistics: Array<StatisticWithForm>, placeholder: boolean = false): Promise<Array<StatisticWithDisplayData>> {
    return new Promise((done) => {
      if (!statistics.length) {
        return done([])
      }
      const requestData: any = {
        kpis: [],
      }

      const kpis = []

      for (const statistic of statistics) {
        kpis.push(statistic.name)
        const state = statistic.form.value

        const kpi: any = {
          id: statistic.name,
        }

        if (statistic.category && state.category?.length) {
          kpi.category = state.category
        }
        if (statistic?.time_frame?.length && state.time_frame) {
          kpi.time_frame = state.time_frame
        }
        if (statistic?.event_size?.length && state.event_size) {
          kpi.event_size = state.event_size
        }
        requestData.kpis.push(kpi)
      }
      const responseKeys = Object.keys(new StatisticResponse())
      const response = kpis
        .map((el) => {
          return `${el}{${responseKeys.join('\n')}}`
        })
        .join('\n')

      const query = `
            query get_client_KPIs($data: clientKPIsInput!) {
              get_client_KPIs(data: $data) {
                  success
                  errorText
                  data {
                      ${response}
                  }
              }
          }
        `
      const cloneStatistics = statistics.map((el) => {
        return Object.assign({}, el)
      })

      if (!placeholder) {
        this.API.privateQuery<any>(query, (data: any) => {
          return {
            data: Object.assign(data, requestData),
          }
        })
          .pipe(
            take(1),
            map((response: any) => {
              if (response.success) {
                const _output: Array<StatisticWithForm> = []
                const output: Array<StatisticWithDisplayData> = []

                Object.entries(omit(response.data, '__typename')).map(([key, data]) => {
                  const statistic = cloneStatistics.find((el) => el.name == key)

                  if (!_output.includes(statistic!) && statistic) {
                    const statisticWithDisplayData = statistic as StatisticWithDisplayData
                    statisticWithDisplayData.displayData = data
                    output.push(statisticWithDisplayData)
                  }
                })
                return output
              } else {
                return [] as Array<StatisticWithDisplayData>
              }
            })
          )
          .subscribe((results) => {
            done(results)
          })
      } else {
        done(
          cloneStatistics.map<StatisticWithDisplayData>((el: StatisticWithForm) => {
            const elWithDb = el as StatisticWithDisplayData
            elWithDb.displayData = {
              first: '0',
              second: '0',
              third: '0',
              date: this.dateService.convertToISOWithTimezone(new Date()).toString(),
              percentage: 0,
            }
            return elWithDb
          })
        )
      }
    })
  }

  getStatistics(activeModuleName: ModulesType): Promise<Statistic[]> {
    return new Promise((done) => {
      this.API.privateQuery<any>(GET_KPIS, (_data: any) => {
        return {
          module: activeModuleName,
        }
      })
        .pipe(
          take(1),
          map((response: any) => {
            if (response.success) {
              return response.data as Array<Statistic>
            } else {
              return [] as Array<Statistic>
            }
          })
        )
        .subscribe((res) => {
          done(res)
        })
    })
  }
}
